'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import type { ComponentProps } from 'react';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { HtmlDescription } from '../homepage/shared/html-description';
import { FiArrowRight } from 'react-icons/fi';
import type { Card } from '@eliadmin/core/src/components/ui/card';

export type BlogCardProps = Omit<ComponentProps<typeof Card>, 'className'> & {
  className?: {
    main?: string;
    link?: string;
    image?: string;
    title?: string;
    date?: string;
    contentContainer?: string;
    content?: string;
  };
  blog: Blog;
  firstBlog?: boolean;
  /** Semantic heading level for the route context; defaults to the homepage section level. */
  titleTag?: 'h2' | 'h3';
};

export function BlogCard({ blog, className, firstBlog = false, titleTag = 'h3' }: BlogCardProps) {
  const TitleTag = titleTag;
  const href = ROUTES.BLOG_DETAIL({ slug: blog.slug });

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-4',
        firstBlog
          ? 'col-span-1 sm:col-span-2 md:mb-2 md:flex-row md:items-center md:justify-between lg:col-span-3 lg:mb-4'
          : 'col-span-1 items-start justify-start',
        className?.main,
      )}
    >
      <Link
        prefetch={false}
        href={href}
        className={cn(
          'relative h-[205px] w-full max-w-[913px] overflow-hidden rounded-[16px]',
          firstBlog ? 'sm:h-[300px] lg:h-[420px] xl:h-[520px]' : 'xl:h-[231px]',
        )}
      >
        <Image
          src={blog?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          fill
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          sizes="(min-width: 768px) 50vw, 100vw"
          className="absolute inset-0 z-0 h-full w-full"
        />
      </Link>
      <div className="flex w-full flex-col items-start justify-start gap-4">
        <TitleTag
          data-ea-visual-tag="h4"
          className={cn(
            'font-heading',
            'w-full font-bold text-foreground',
            'line-clamp-2',
            firstBlog
              ? 'h-[48px] text-base sm:h-[52px] sm:text-lg lg:h-[64px] lg:text-2xl xl:h-[72px] xl:text-3xl'
              : 'h-[48px] text-base lg:h-[54px] lg:text-lg',
          )}
        >
          <Link
            prefetch={false}
            href={href}
            className="line-clamp-2 rounded-sm transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {blog.name}
          </Link>
        </TitleTag>
        <HtmlDescription
          size="sm"
          color="foreground"
          content={blog.content}
          className="ea-blog-card__description line-clamp-3 h-[48px] w-full text-left sm:h-[60px] xl:h-[60px]"
        />
        {/* <div className="flex items-center justify-start gap-2">
          <Image
            src={'/images/placeholder.png'}
            alt={'Autor článku'}
            width={0}
            height={0}
            className="min-h-[28px] min-w-[28px] !overflow-hidden rounded-full"
            sizes="28px"
          />
          <p className="text-xs font-medium text-muted-foreground">Foulcher Nathanil</p>
          <p className="text-xs font-medium text-muted-foreground">·</p>
          <p className="text-xs font-medium text-muted-foreground">
            {new Date(blog.created_at).toLocaleDateString(locale)}
          </p>
        </div> */}
        <div className="flex w-full items-center justify-end">
          <Link
            prefetch={false}
            href={href}
            className="flex items-center justify-end gap-2 text-base font-medium text-primary"
          >
            Prečítať článok <FiArrowRight size={20} />
          </Link>
        </div>
      </div>
    </div>
  );
}
